import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  Tooltip, 
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import rateLimitService from '../../services/rateLimitService';

const RateLimitInfo = () => {
  const [rateLimitData, setRateLimitData] = useState(null);
  const [warnings, setWarnings] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRateLimitInfo = () => {
    setLoading(true);
    try {
      const data = rateLimitService.getRateLimitInfo();
      const warningData = rateLimitService.checkRateLimitWarnings();
      setRateLimitData(data);
      setWarnings(warningData);
    } catch (err) {
      console.error('Error getting rate limit info:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRateLimitInfo();
    
    // Refresh rate limit info every minute
    const intervalId = setInterval(fetchRateLimitInfo, 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const renderLimitItem = (title, used, remaining, total, description, warning) => (
    <Grid item xs={12} md={4}>
      <Box sx={{ 
        p: 2,
        bgcolor: warning ? 'warning.light' : 'transparent',
        transition: 'background-color 0.3s ease'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" color={warning ? 'warning.dark' : 'inherit'}>
            {title}
          </Typography>
          {warning && (
            <Tooltip title="¡Cerca del límite!" arrow>
              <WarningIcon color="warning" sx={{ ml: 1 }} />
            </Tooltip>
          )}
          <Tooltip title={description} arrow>
            <IconButton size="small" sx={{ ml: 0.5 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Usados:</Typography>
          <Typography variant="body2" fontWeight="medium">{used}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Restantes:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="medium" 
            color={warning ? 'warning.dark' : 'primary'}
          >
            {remaining}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Total:</Typography>
          <Typography variant="body2" fontWeight="medium">{total}</Typography>
        </Box>
      </Box>
    </Grid>
  );

  if (loading && !rateLimitData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'primary.contrastText'
      }}>
        <Typography variant="h6">
          Límites de API Apollo
        </Typography>
        <Tooltip title="Actualizar información">
          <IconButton 
            onClick={fetchRateLimitInfo} 
            disabled={loading}
            sx={{ color: 'white' }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      
      {warnings && (warnings.minuteWarning || warnings.hourlyWarning || warnings.dailyWarning) && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Se están alcanzando los límites de la API. Por favor, espere antes de realizar más solicitudes.
        </Alert>
      )}
      
      {rateLimitData && (
        <Grid container>
          {renderLimitItem(
            'Por Minuto', 
            rateLimitData.minuteUsage || 'N/A', 
            rateLimitData.minuteRequestsLeft || 'N/A', 
            rateLimitData.rateLimitMinute || 'N/A',
            'Número de solicitudes permitidas por minuto',
            warnings?.minuteWarning
          )}
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
          <Divider sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }} />
          
          {renderLimitItem(
            'Por Hora', 
            rateLimitData.hourlyUsage || 'N/A', 
            rateLimitData.hourlyRequestsLeft || 'N/A', 
            rateLimitData.rateLimitHourly || 'N/A',
            'Número de solicitudes permitidas por hora',
            warnings?.hourlyWarning
          )}
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
          <Divider sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }} />
          
          {renderLimitItem(
            'Por Día', 
            rateLimitData.dailyUsage || 'N/A', 
            rateLimitData.dailyRequestsLeft || 'N/A', 
            rateLimitData.rateLimitDaily || 'N/A',
            'Número de solicitudes permitidas por día',
            warnings?.dailyWarning
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default RateLimitInfo; 